import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import api from '../services/api';
import SEO from '../components/SEO';
import { formatPrice } from '../utils/format';
import { Skeleton } from '../components/primitives';
import './Account.css';

const Account = () => {
  const { t, i18n } = useTranslation();
  const { currencyLabel: currency } = useSiteSettings();
  const navigate = useNavigate();
  const { customer, token, isAuthenticated, logout, updateProfile, loading: authLoading } = useCustomerAuth();

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const [editMode, setEditMode] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', phone: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [ordersPage, setOrdersPage] = useState(1);

  const ORDERS_PER_PAGE = 10;
  const ORDER_STATUS_OPTIONS = [
    { value: 'all', labelKey: 'account.orderFilterAll', labelDefault: 'All' },
    { value: 'pending', labelKey: 'account.orderFilterPending', labelDefault: 'Pending' },
    { value: 'processing', labelKey: 'account.orderFilterProcessing', labelDefault: 'Processing' },
    { value: 'shipped', labelKey: 'account.orderFilterShipped', labelDefault: 'Shipped' },
    { value: 'delivered', labelKey: 'account.orderFilterDelivered', labelDefault: 'Delivered' },
    { value: 'cancelled', labelKey: 'account.orderFilterCancelled', labelDefault: 'Cancelled' },
  ];

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/api/customer/orders', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/account' } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated, fetchOrders]);

  useEffect(() => {
    if (customer) {
      setProfileData({ name: customer.name || '', phone: customer.phone || '' });
    }
  }, [customer]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase().replace(/^#/, '');
    return orders.filter((order) => {
      const matchesStatus =
        orderStatusFilter === 'all' || order.status === orderStatusFilter;
      if (!matchesStatus) return false;
      if (!q) return true;
      const num = String(order.orderNumber || '').toLowerCase();
      return num.includes(q);
    });
  }, [orders, orderSearch, orderStatusFilter]);

  const ordersTotalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
  const paginatedOrders = useMemo(() => {
    const safePage = Math.min(ordersPage, ordersTotalPages);
    const start = (safePage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, ordersPage, ordersTotalPages]);

  useEffect(() => {
    setOrdersPage(1);
  }, [orderSearch, orderStatusFilter]);

  useEffect(() => {
    if (ordersPage > ordersTotalPages) {
      setOrdersPage(ordersTotalPages);
    }
  }, [ordersPage, ordersTotalPages]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileMessage('');
    const result = await updateProfile(profileData);
    if (result.success) {
      setEditMode(false);
      setProfileMessage(t('account.profileUpdated', 'Profile updated successfully'));
      setTimeout(() => setProfileMessage(''), 3000);
    } else {
      setProfileMessage(result.error);
    }
    setProfileSaving(false);
  };

  const fmtPrice = (v) => formatPrice(v, { lang: i18n.language });

  const statusBadgeClass = (status) => {
    const map = {
      pending: 'status-pending',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled',
    };
    return map[status] || 'status-pending';
  };

  if (authLoading) {
    return (
      <div className="account-page" aria-busy="true">
        <div className="container">
          <Skeleton.Block height="2rem" width="220px" rounded="md" />
          <Skeleton.Line width="40%" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="account-page">
      <SEO title={t('account.title', 'My Account')} />

      <div className="account-container">
        <div className="account-header">
          <div>
            <h1>{t('account.greeting', 'Hello')}, {customer?.name}</h1>
            <p className="account-email">{customer?.email}</p>
          </div>
          <button onClick={handleLogout} className="account-logout-btn">
            {t('account.logout', 'Logout')}
          </button>
        </div>

        <div className="account-tabs">
          <button
            className={`account-tab ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('orders')}
          >
            {t('account.orders', 'My Orders')}
          </button>
          <button
            className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('account.profile', 'Profile')}
          </button>
        </div>

        {activeTab === 'orders' && (
          <div className="account-orders">
            {ordersLoading ? (
              <div aria-busy="true">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton.Block key={i} height="80px" rounded="md" style={{ marginBottom: 'var(--space-3)' }} />
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="account-empty">
                <p>{t('account.noOrders', "You haven't placed any orders yet.")}</p>
                <Link to="/products" className="auth-submit-btn auth-submit-btn--link">
                  {t('account.startShopping', 'Start Shopping')}
                </Link>
              </div>
            ) : (
              <>
                <div className="account-orders-toolbar">
                  <input
                    type="search"
                    className="account-orders-search"
                    placeholder={t('account.orderSearchPlaceholder', 'Search by order number')}
                    value={orderSearch}
                    onChange={(e) => setOrderSearch(e.target.value)}
                    aria-label={t('account.orderSearchPlaceholder', 'Search by order number')}
                  />
                  <div className="order-status-pills" role="group" aria-label={t('account.orderStatusFilter', 'Filter by status')}>
                    {ORDER_STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={`order-status-pill ${orderStatusFilter === opt.value ? 'active' : ''}`}
                        onClick={() => setOrderStatusFilter(opt.value)}
                      >
                        {t(opt.labelKey, opt.labelDefault)}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredOrders.length === 0 ? (
                  <div className="account-empty account-orders-no-match">
                    <p>{t('account.noOrdersMatch', 'No orders match your search or filter.')}</p>
                  </div>
                ) : (
                  <>
                    <div className="orders-list">
                      {paginatedOrders.map((order) => (
                        <Link to={`/account/orders/${order.id}`} key={order.id} className="order-card">
                          <div className="order-card-top">
                            <span className="order-number">#{order.orderNumber}</span>
                            <span className={`order-status-badge ${statusBadgeClass(order.status)}`}>
                              {t(`order.status.${order.status}`, order.status)}
                            </span>
                          </div>
                          <div className="order-card-details">
                            <span className="order-date">
                              {new Date(order.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric', month: 'short', day: 'numeric'
                              })}
                            </span>
                            <span className="order-items-count">
                              {order.itemCount} {order.itemCount === 1 ? t('account.item', 'item') : t('account.items', 'items')}
                            </span>
                            <span className="order-total">{fmtPrice(order.total)} {currency}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {ordersTotalPages > 1 && (
                      <div className="orders-pagination">
                        <button
                          type="button"
                          className="orders-page-btn"
                          disabled={ordersPage <= 1}
                          onClick={() => setOrdersPage((p) => Math.max(1, p - 1))}
                        >
                          {t('account.ordersPrev', 'Previous')}
                        </button>
                        <span className="orders-page-info">
                          {t('account.ordersPageOf', 'Page {{current}} of {{total}}', {
                            current: Math.min(ordersPage, ordersTotalPages),
                            total: ordersTotalPages,
                          })}
                        </span>
                        <button
                          type="button"
                          className="orders-page-btn"
                          disabled={ordersPage >= ordersTotalPages}
                          onClick={() => setOrdersPage((p) => Math.min(ordersTotalPages, p + 1))}
                        >
                          {t('account.ordersNext', 'Next')}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="account-profile">
            {profileMessage && (
              <div className={`profile-message ${profileMessage.includes('success') || profileMessage.includes('updated') ? 'success' : 'error'}`}>
                {profileMessage}
              </div>
            )}

            <div className="profile-card">
              <div className="profile-field">
                <label>{t('checkout.name', 'Name')}</label>
                {editMode ? (
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  />
                ) : (
                  <span>{customer?.name}</span>
                )}
              </div>

              <div className="profile-field">
                <label>{t('checkout.email', 'Email')}</label>
                <span>{customer?.email}</span>
              </div>

              <div className="profile-field">
                <label>{t('checkout.phone', 'Phone')}</label>
                {editMode ? (
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  />
                ) : (
                  <span>{customer?.phone || '-'}</span>
                )}
              </div>

              <div className="profile-actions">
                {editMode ? (
                  <>
                    <button onClick={handleProfileSave} className="auth-submit-btn" disabled={profileSaving}>
                      {profileSaving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                    </button>
                    <button onClick={() => { setEditMode(false); setProfileData({ name: customer?.name || '', phone: customer?.phone || '' }); }} className="account-cancel-btn">
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </>
                ) : (
                  <button onClick={() => setEditMode(true)} className="auth-submit-btn">
                    {t('account.editProfile', 'Edit Profile')}
                  </button>
                )}
              </div>
            </div>

            <div className="profile-card">
              <h3>{t('account.security', 'Security')}</h3>
              <Link to="/forgot-password" className="change-password-link">
                {t('account.changePassword', 'Change Password')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Account;
